import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import toast from "react-hot-toast";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import { getReceiptForDownload } from "./feeReceiptUtils";
import { downloadEmsFeeReceiptPdf } from "./feeReceiptPdf";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { hasPermissionAction } from "../../../Utils/organizationPermissions";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";

function formatDisplayDate(value) {
  if (value == null || value === "") return "—";
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  return s;
}

function fmtMoney(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

/** e.g. `12,498 NZD` — omits code if missing. */
function fmtMoneyWithCurrencyCode(amount, currencyCode) {
  const m = fmtMoney(amount);
  if (m === "—") return "—";
  const c =
    currencyCode != null && String(currencyCode).trim() !== ""
      ? String(currencyCode).trim()
      : "";
  return c ? `${m} ${c}` : m;
}

function studentName(st) {
  if (!st) return "—";
  const parts = [st.first_name, st.middle_name, st.last_name].filter(
    (x) => x && String(x).trim() !== ""
  );
  if (parts.length) return parts.join(" ");
  if (st.full_name) return String(st.full_name);
  return "—";
}

function pickFirstReceipt(sfList) {
  if (!Array.isArray(sfList) || sfList.length === 0) return null;
  return sfList[0];
}

function sumReceiptsPaidStudentCurrency(sfList) {
  if (!Array.isArray(sfList) || !sfList.length) return 0;
  let t = 0;
  for (const sf of sfList) {
    const v = Number(sf?.amount_paid_student_currency);
    if (Number.isFinite(v)) t += v;
  }
  return t;
}

function sumReceiptsPaidInr(sfList) {
  if (!Array.isArray(sfList) || !sfList.length) return 0;
  let t = 0;
  for (const sf of sfList) {
    const v = Number(sf?.amount_received_inr);
    if (Number.isFinite(v)) t += v;
  }
  return t;
}

function receiptNumber(sf) {
  const n =
    sf?.payment_number ??
    sf?.receipt_number ??
    sf?.student_fee_payment_number;
  return n != null && String(n).trim() !== "" ? String(n).trim() : "—";
}

function paymentDateOnly(sf) {
  return formatDisplayDate(sf?.payment_date);
}

function settlementDateOnly(sf) {
  return formatDisplayDate(sf?.settlement_date ?? sf?.receipt_date);
}

/** Student currency for a fee row (may differ from admission.currency_code, e.g. NZD vs CAD). */
function studentCcyForReceipt(sf, inst, admission) {
  const c =
    sf?.student_currency_code ??
    inst?.student_currency_code ??
    admission?.currency_code;
  return c != null && String(c).trim() !== "" ? String(c).trim() : "—";
}

function receiptStatus(sf) {
  const s =
    sf?.payment_status ??
    sf?.status ??
    sf?.receipt_status ??
    sf?.settlement_status;
  return s != null && String(s).trim() !== "" ? String(s) : "—";
}

function organizationDisplayName(admission) {
  const o = admission?.organization;
  if (!o) return "—";
  const n = o.organization_name ?? o.organization_short_name;
  return n != null && String(n).trim() !== "" ? String(n).trim() : "—";
}

async function downloadFeeReceiptPdf(admission, sf, inst, organizationId) {
  if (!admission || !sf) {
    toast.error("Receipt data is not available.");
    return;
  }

  try {
    const receiptData = await getReceiptForDownload(
      admission,
      sf,
      inst,
      organizationId
    );
    if (!receiptData) {
      toast.error("Could not load receipt data.");
      return;
    }
    const raw = receiptData.receiptNo;
    const safe =
      raw !== "—" ? String(raw).replace(/[^\w.-]+/g, "_") : "payment";
    await downloadEmsFeeReceiptPdf(receiptData, `fee-receipt-${safe}.pdf`);
    toast.success("Receipt downloaded.");
  } catch (err) {
    console.error("Fee receipt PDF failed:", err);
    toast.error("Could not download receipt PDF.");
  }
}

async function emailFeeReceipt(sf, organizationId) {
  const feeId = sf?.organization_ems_student_fee_id;
  if (!feeId) {
    toast.error("Receipt is not available to email.");
    return;
  }

  try {
    const res = await axios.post(
      `${MAIN_URL}/api/ems/students-fees/${feeId}/send-email`,
      { organization_id: organizationId ? Number(organizationId) : undefined },
      { headers: getApiHeaders() }
    );
    if (res?.data?.success === false) {
      toast.error(res?.data?.message || "Could not send fee receipt email.");
      return;
    }
    toast.success(res?.data?.message || "Fee receipt email sent.");
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      "Could not send fee receipt email.";
    toast.error(msg);
  }
}

/** Draw a bordered grid table; supports column width fractions (sum 1). Returns next Y (mm). */
function drawPdfTable(doc, x, yStart, totalW, headers, body, pageH, margin, opts = {}) {
  const fontSize = opts.fontSize ?? 8;
  const maxCellLines = opts.maxCellLines ?? 4;
  const n = headers.length;
  const fractions = opts.fractions?.length === n ? opts.fractions : Array(n).fill(1 / n);
  const colW = fractions.map((f) => f * totalW);
  const lineMm = fontSize * 0.38;
  const pad = 1;

  let y = yStart;

  const measureCells = (cells) => {
    let maxH = lineMm + 2 * pad;
    cells.forEach((cell, i) => {
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(String(cell), colW[i] - 2 * pad);
      const used = Math.min(lines.length, maxCellLines);
      const h = used * lineMm + 2 * pad;
      maxH = Math.max(maxH, h);
    });
    return Math.min(maxH, 24);
  };

  const drawCells = (cells, bold) => {
    const h = measureCells(cells);
    if (y + h > pageH - margin - 1) {
      doc.addPage();
      y = margin;
    }
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(fontSize);
    doc.setDrawColor(190);
    let cx = x;
    cells.forEach((cell, i) => {
      doc.rect(cx, y, colW[i], h);
      const lines = doc
        .splitTextToSize(String(cell), colW[i] - 2 * pad)
        .slice(0, maxCellLines);
      doc.text(lines, cx + pad, y + lineMm + pad - 0.5);
      cx += colW[i];
    });
    doc.setDrawColor(0);
    y += h;
  };

  drawCells(headers, true);
  body.forEach((row) => drawCells(row, false));
  return y;
}

/** Full Fee Account view as landscape PDF (summary + installments + totals). */
function downloadFeeAccountFullPdf({
  admission,
  installments,
  totals,
  balanceInr,
  balanceStudent,
  tpName,
  studentCurr,
}) {
  if (!admission) return;

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const innerW = pageW - 2 * margin;
  let y = margin;

  const org = organizationDisplayName(admission);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(org, pageW / 2, y, { align: "center" });
  y += 7;
  doc.setFontSize(15);
  doc.text("Fee Account", pageW / 2, y, { align: "center" });
  y += 9;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Student: ${studentName(admission.student)}`, margin, y);
  y += 5;
  doc.text(
    `Admission: ${admission.admission_number ?? "—"} · ${formatDisplayDate(admission.admission_date)}`,
    margin,
    y
  );
  y += 7;

  doc.setFont("helvetica", "bold");
  doc.text("Training program", margin, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  const tpLines = doc.splitTextToSize(String(tpName), innerW);
  doc.text(tpLines, margin, y);
  y += tpLines.length * 4.2 + 5;

  if (y > pageH - margin - 40) {
    doc.addPage();
    y = margin;
  }

  const sumAmtHeader =
    studentCurr !== "—" ? `Amount (${studentCurr})` : "Amount (student)";
  const summaryHead = ["", "INR", sumAmtHeader];
  const summaryBody = [
    [
      "Total fee",
      fmtMoney(admission.total_fee_amount),
      fmtMoneyWithCurrencyCode(
        admission.total_fee_amount_student_currency,
        studentCurr
      ),
    ],
    [
      "Discount",
      fmtMoney(admission.discount_amount),
      fmtMoneyWithCurrencyCode(
        admission.discount_amount_student_currency,
        studentCurr
      ),
    ],
    [
      "Net fee",
      fmtMoney(admission.net_fee_amount),
      fmtMoneyWithCurrencyCode(
        admission.net_fee_amount_student_currency,
        studentCurr
      ),
    ],
  ];
  y = drawPdfTable(doc, margin, y, innerW, summaryHead, summaryBody, pageH, margin, {
    fontSize: 9,
    fractions: [0.26, 0.37, 0.37],
  });
  y += 7;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Installments", margin, y);
  y += 6;

  const instHead = [
    "#",
    "Due date",
    "Fee (INR)",
    "Fee (student)",
    "Status",
    "Receipt #",
    "Payment date",
    "Settlement",
    "Pay mode",
    "Rcpt status",
    "Rcpts",
  ];

  const instBody = installments.map((inst) => {
    const sfs = inst?.student_fees ?? inst?.studentFees ?? [];
    const rec = pickFirstReceipt(sfs);
    const feeStu =
      inst?.fee_instalment_student_student_currency ??
      inst?.fee_instalment_student_currency;
    const instCcy = inst?.student_currency_code ?? studentCurr;
    const receiptNum =
      sfs.length > 1 && rec
        ? `${receiptNumber(rec)} (+${sfs.length - 1})`
        : rec
          ? receiptNumber(rec)
          : "—";
    return [
      String(inst.installment_number ?? "—"),
      formatDisplayDate(inst.due_date),
      fmtMoney(inst?.fee_instalment_inr),
      fmtMoneyWithCurrencyCode(feeStu, instCcy),
      String(inst.status ?? "—"),
      receiptNum,
      rec ? paymentDateOnly(rec) : "—",
      rec ? settlementDateOnly(rec) : "—",
      rec?.payment_mode != null && String(rec.payment_mode).trim() !== ""
        ? String(rec.payment_mode)
        : "—",
      rec ? receiptStatus(rec) : "—",
      sfs.length > 0 ? String(sfs.length) : "—",
    ];
  });

  if (instBody.length === 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("No installments", margin, y);
    y += 6;
  } else {
    y = drawPdfTable(doc, margin, y, innerW, instHead, instBody, pageH, margin, {
      fontSize: 7,
      maxCellLines: 3,
      fractions: [
        0.05, 0.09, 0.1, 0.12, 0.08, 0.09, 0.09, 0.09, 0.1, 0.09, 0.1,
      ],
    });
  }

  y += 8;
  if (y > pageH - margin - 30) {
    doc.addPage();
    y = margin;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Summary", margin, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  const rightBlock = (label, v1, v2) => {
    doc.text(label, margin, y);
    doc.text(String(v1), pageW - margin - 52, y, { align: "right" });
    doc.text(String(v2), pageW - margin, y, { align: "right" });
    y += 5;
  };

  rightBlock(
    "Total installments (INR) — scheduled / paid",
    fmtMoney(totals.sumInstInr),
    fmtMoney(totals.sumPaidInr)
  );
  rightBlock(
    `Total installments (${studentCurr}) — scheduled / paid`,
    fmtMoney(totals.sumInstStudent),
    fmtMoney(totals.sumPaidStudent)
  );
  y += 2;
  doc.setDrawColor(180);
  doc.line(margin, y, pageW - margin, y);
  y += 4;
  doc.text("Balance (INR)", margin, y);
  doc.text(
    balanceInr != null ? fmtMoney(balanceInr) : "—",
    pageW - margin,
    y,
    { align: "right" }
  );
  y += 5;
  doc.text(`Balance (${studentCurr})`, margin, y);
  doc.text(
    balanceStudent != null ? fmtMoney(balanceStudent) : "—",
    pageW - margin,
    y,
    { align: "right" }
  );

  const pageCount =
    typeof doc.getNumberOfPages === "function"
      ? doc.getNumberOfPages()
      : doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageW / 2,
      pageH - 6,
      { align: "center" }
    );
    doc.setTextColor(0);
  }

  const base =
    admission.admission_number != null
      ? `fee-account-${String(admission.admission_number).replace(/\s+/g, "_")}`
      : "fee-account";
  doc.save(`${base}.pdf`);
}

export default function AdmissionFeePortalDialog({
  open,
  onClose,
  loading,
  admission,
  organizationId,
}) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const { Permission } = usePermissionDataStore();
  const canDownloadFeeReceipt = useMemo(
    () => hasPermissionAction(Permission, "DOWNLOAD_FEE_RECEIPT"),
    [Permission]
  );
  const [receiptDownloadingId, setReceiptDownloadingId] = useState(null);
  const [receiptEmailingId, setReceiptEmailingId] = useState(null);
  const tpName =
    admission?.training_program?.training_program_name ||
    admission?.training_program_name ||
    "—";

  const studentCurr = admission?.currency_code || "—";

  const installments = useMemo(() => {
    const raw =
      admission?.fee_installments ??
      admission?.feeInstallments ??
      [];
    if (!Array.isArray(raw)) return [];
    return [...raw].sort(
      (a, b) =>
        Number(a?.installment_number ?? 0) - Number(b?.installment_number ?? 0)
    );
  }, [admission]);

  const totals = useMemo(() => {
    let sumInstInr = 0;
    let sumInstStudent = 0;
    let sumPaidInr = 0;
    let sumPaidStudent = 0;
    for (const inst of installments) {
      const inr = Number(inst?.fee_instalment_inr);
      const stu = Number(
        inst?.fee_instalment_student_student_currency ??
          inst?.fee_instalment_student_currency
      );
      if (Number.isFinite(inr)) sumInstInr += inr;
      if (Number.isFinite(stu)) sumInstStudent += stu;
      const sfs = inst?.student_fees ?? inst?.studentFees;
      sumPaidInr += sumReceiptsPaidInr(sfs);
      sumPaidStudent += sumReceiptsPaidStudentCurrency(sfs);
    }
    const netInr = Number(admission?.net_fee_amount);
    const netStudent = Number(admission?.net_fee_amount_student_currency);
    return {
      sumInstInr,
      sumInstStudent,
      sumPaidInr,
      sumPaidStudent,
      netInr: Number.isFinite(netInr) ? netInr : null,
      netStudent: Number.isFinite(netStudent) ? netStudent : null,
    };
  }, [installments, admission]);

  const balanceStudent =
    totals.netStudent != null
      ? totals.netStudent - totals.sumPaidStudent
      : null;
  const balanceInr =
    totals.netInr != null ? totals.netInr - totals.sumPaidInr : null;

  const handleDownloadFullPdf = useCallback(() => {
    if (!canDownloadFeeReceipt) {
      toast.error("You do not have permission to download fee receipts.");
      return;
    }
    downloadFeeAccountFullPdf({
      admission,
      installments,
      totals,
      balanceInr,
      balanceStudent,
      tpName,
      studentCurr,
    });
  }, [
    canDownloadFeeReceipt,
    admission,
    installments,
    totals,
    balanceInr,
    balanceStudent,
    tpName,
    studentCurr,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isNarrow}
      scroll="paper"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="span">
          Fee Account
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress size={36} />
          </Box>
        )}
        {!loading && admission && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ textAlign: "center", pb: 0.5 }}>
              <Typography variant="h6" fontWeight={700} component="div">
                {organizationDisplayName(admission)}
              </Typography>
              {admission?.organization?.organization_short_name &&
                organizationDisplayName(admission) !==
                  String(admission.organization.organization_short_name).trim() && (
                  <Typography variant="caption" color="text.secondary">
                    {admission.organization.organization_short_name}
                  </Typography>
                )}
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Student
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {studentName(admission.student)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Admission
              </Typography>
              <Typography variant="body1">
                {admission.admission_number != null
                  ? String(admission.admission_number)
                  : "—"}
                {admission.admission_date
                  ? ` · ${formatDisplayDate(admission.admission_date)}`
                  : ""}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Training program
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
                {tpName}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: "34%", px: 1 }} />
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, width: "33%", px: 1, whiteSpace: "nowrap" }}
                      >
                        INR
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, width: "33%", px: 1, whiteSpace: "nowrap" }}
                      >
                        {studentCurr !== "—" ? studentCurr : "Student"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ["Total fee", admission.total_fee_amount, admission.total_fee_amount_student_currency, false],
                      ["Discount", admission.discount_amount, admission.discount_amount_student_currency, false],
                      ["Net fee", admission.net_fee_amount, admission.net_fee_amount_student_currency, true],
                    ].map(([label, inr, studentAmt, strong]) => (
                      <TableRow key={label}>
                        <TableCell sx={{ fontWeight: strong ? 600 : 400, px: 1 }}>
                          {label}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: strong ? 600 : 400, px: 1, whiteSpace: "nowrap" }}
                        >
                          {fmtMoney(inr)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: strong ? 600 : 400, px: 1, whiteSpace: "nowrap" }}
                        >
                          {fmtMoney(studentAmt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Installments
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 0.25 }}>
              {tpName}
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Due date</TableCell>
                    <TableCell align="right">Fee (INR)</TableCell>
                    <TableCell align="right">Fee (student)</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Receipt #</TableCell>
                    <TableCell>Payment date</TableCell>
                    <TableCell>Settlement date</TableCell>
                    <TableCell>Payment mode</TableCell>
                    <TableCell>Receipt status</TableCell>
                    {canDownloadFeeReceipt ? (
                      <TableCell align="center" width={72}>
                        PDF
                      </TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {installments.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={canDownloadFeeReceipt ? 11 : 10}
                        align="center"
                      >
                        No installments
                      </TableCell>
                    </TableRow>
                  ) : (
                    installments.map((inst) => {
                      const sfs = inst?.student_fees ?? inst?.studentFees ?? [];
                      const rec = pickFirstReceipt(sfs);
                      const feeInr = inst?.fee_instalment_inr;
                      const feeStu =
                        inst?.fee_instalment_student_student_currency ??
                        inst?.fee_instalment_student_currency;
                      const instCcy =
                        inst?.student_currency_code ?? studentCurr;
                      const receiptNum =
                        sfs.length > 1 && rec
                          ? `${receiptNumber(rec)} (+${sfs.length - 1})`
                          : rec
                            ? receiptNumber(rec)
                            : "—";
                      return (
                        <TableRow key={inst.organization_ems_fee_installment_id ?? inst.installment_number}>
                          <TableCell>{inst.installment_number ?? "—"}</TableCell>
                          <TableCell>{formatDisplayDate(inst.due_date)}</TableCell>
                          <TableCell align="right">{fmtMoney(feeInr)}</TableCell>
                          <TableCell align="right">
                            {fmtMoneyWithCurrencyCode(feeStu, instCcy)}
                          </TableCell>
                          <TableCell>{inst.status ?? "—"}</TableCell>
                          <TableCell>{receiptNum}</TableCell>
                          <TableCell>
                            {rec ? paymentDateOnly(rec) : "—"}
                          </TableCell>
                          <TableCell>
                            {rec ? settlementDateOnly(rec) : "—"}
                          </TableCell>
                          <TableCell>
                            {rec?.payment_mode != null &&
                            String(rec.payment_mode).trim() !== ""
                              ? String(rec.payment_mode)
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {rec ? receiptStatus(rec) : "—"}
                          </TableCell>
                          {canDownloadFeeReceipt ? (
                            <TableCell align="center" sx={{ py: 0.5 }}>
                              {sfs.length === 0 ||
                              String(inst?.status ?? "").toLowerCase() !==
                                "paid" ? (
                                "—"
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 0.25,
                                    justifyContent: "center",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {sfs.map((sf, i) => {
                                    const feeId =
                                      sf.organization_ems_student_fee_id ?? i;
                                    const isDownloading =
                                      receiptDownloadingId === feeId;
                                    const isEmailing =
                                      receiptEmailingId === feeId;
                                    return (
                                      <Box
                                        key={feeId}
                                        sx={{ display: "inline-flex", gap: 0.25 }}
                                      >
                                        <Tooltip
                                          title={
                                            sfs.length > 1
                                              ? `Download receipt · ${receiptNumber(sf)}`
                                              : "Download fee receipt (PDF)"
                                          }
                                        >
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label={`Download receipt PDF ${receiptNumber(sf)}`}
                                              disabled={isDownloading || isEmailing}
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                setReceiptDownloadingId(feeId);
                                                try {
                                                  await downloadFeeReceiptPdf(
                                                    admission,
                                                    sf,
                                                    inst,
                                                    organizationId
                                                  );
                                                } finally {
                                                  setReceiptDownloadingId(null);
                                                }
                                              }}
                                            >
                                              {isDownloading ? (
                                                <CircularProgress size={18} />
                                              ) : (
                                                <DownloadIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Tooltip
                                          title={
                                            sfs.length > 1
                                              ? `Email receipt · ${receiptNumber(sf)}`
                                              : "Email fee receipt to student"
                                          }
                                        >
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="secondary"
                                              aria-label={`Email receipt ${receiptNumber(sf)}`}
                                              disabled={isDownloading || isEmailing}
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                setReceiptEmailingId(feeId);
                                                try {
                                                  await emailFeeReceipt(
                                                    sf,
                                                    organizationId
                                                  );
                                                } finally {
                                                  setReceiptEmailingId(null);
                                                }
                                              }}
                                            >
                                              {isEmailing ? (
                                                <CircularProgress size={18} />
                                              ) : (
                                                <EmailIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                            </TableCell>
                          ) : null}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>

            <Divider />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isNarrow
                  ? "minmax(0, 1fr) auto auto"
                  : "minmax(0, 1fr) 110px 110px",
                columnGap: 1.5,
                rowGap: 0.75,
                alignItems: "baseline",
                fontSize: "0.9rem",
                width: "100%",
              }}
            >
              <span />
              <Box component="span" sx={{ textAlign: "right", color: "text.secondary", fontSize: "0.75rem" }}>
                Scheduled
              </Box>
              <Box component="span" sx={{ textAlign: "right", color: "text.secondary", fontSize: "0.75rem" }}>
                Paid
              </Box>
              <span>Total installments (INR)</span>
              <Box component="span" sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {fmtMoney(totals.sumInstInr)}
              </Box>
              <Box component="span" sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {fmtMoney(totals.sumPaidInr)}
              </Box>
              <span>Total installments ({studentCurr})</span>
              <Box component="span" sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {fmtMoney(totals.sumInstStudent)}
              </Box>
              <Box component="span" sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {fmtMoney(totals.sumPaidStudent)}
              </Box>
              <Divider sx={{ gridColumn: "1 / -1", my: 0.5 }} />
              <span>Balance (INR)</span>
              <Box component="span" sx={{ gridColumn: "2 / -1", textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>
                {balanceInr != null ? fmtMoney(balanceInr) : "—"}
              </Box>
              <span>Balance ({studentCurr})</span>
              <Box component="span" sx={{ gridColumn: "2 / -1", textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>
                {balanceStudent != null ? fmtMoney(balanceStudent) : "—"}
              </Box>
            </Box>
          </Box>
        )}
        {!loading && !admission && open && (
          <Typography color="text.secondary">No data.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        {canDownloadFeeReceipt ? (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            disabled={loading || !admission}
            onClick={handleDownloadFullPdf}
          >
            Download full PDF
          </Button>
        ) : null}
        <Box sx={{ flex: 1, minWidth: 8 }} />
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
